import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store';
import { ALL_FLOWS } from '../../config/navigationFlow';
import type { AppFieldKey } from '../../config/fieldConfig';
import { useSaveUserProfile } from '../../hooks/useSaveUserProfile';
import { useSaveDesiredPartnerProfile } from '../../hooks/useSaveDesiredPartnerProfile';
import Toast from '../Toast/Toast';

interface FlowRouteProps {
  fieldKey: AppFieldKey;
  render: (goBack: () => void, goNext: () => void) => React.ReactElement;
}

const TOAST_NAVIGATE_DELAY_MS = 1500;

const FlowRoute: React.FC<FlowRouteProps> = ({ fieldKey, render }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visibility = useAppSelector((s) => s.appConfig.visibility);
  const { saveUserProfile, toast: userProfileToast, clearToast: clearUserProfileToast } = useSaveUserProfile();
  const { saveDesiredPartnerProfile, toast: desiredPartnerToast, clearToast: clearDesiredPartnerToast } = useSaveDesiredPartnerProfile();

  // Whichever hook fired last will have an active toast
  const toast = userProfileToast ?? desiredPartnerToast;
  const clearToast = userProfileToast ? clearUserProfileToast : clearDesiredPartnerToast;

  const isStepVisible = (key: number) => visibility[key] ?? true;
  const isHidden = !isStepVisible(fieldKey);

  const currentFlow = ALL_FLOWS.find((flow) =>
    flow.some((s) => s.path === pathname)
  ) ?? [];
  const currentIndex = currentFlow.findIndex((s) => s.path === pathname);

  const runSkipSave = async (
    saveType: 'userProfile' | 'desiredPartner' | undefined,
    destination: string
  ) => {
    try {
      if (saveType === 'desiredPartner') {
        await saveDesiredPartnerProfile();
      } else {
        await saveUserProfile();
      }
    } catch {
      return; // toast already set inside hook — stay on current page
    }
    setTimeout(() => navigate(destination), TOAST_NAVIGATE_DELAY_MS);
  };

  const goNext = async () => {
    let skippedStep: { saveOnSkip?: boolean; saveType?: 'userProfile' | 'desiredPartner' } | null = null;
    let targetPath: string | null = null;

    for (let i = currentIndex + 1; i < currentFlow.length; i++) {
      if (isStepVisible(currentFlow[i].fieldKey)) {
        targetPath = currentFlow[i].path;
        break;
      }
      if (currentFlow[i].saveOnSkip) {
        skippedStep = currentFlow[i];
      }
    }

    const exitPath = targetPath ? undefined : currentFlow.at(-1)?.exitPath;
    const destination = targetPath ?? exitPath;

    if (skippedStep && destination) {
      await runSkipSave(skippedStep.saveType, destination);
    } else if (destination) {
      navigate(destination);
    }
  };

  const goBack = () => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (isStepVisible(currentFlow[i].fieldKey)) {
        navigate(currentFlow[i].path);
        return;
      }
    }
    navigate(-1 as any);
  };

  // If this page is hidden, redirect to the next visible step
  if (isHidden) {
    for (let i = currentIndex + 1; i < currentFlow.length; i++) {
      if (isStepVisible(currentFlow[i].fieldKey)) {
        return <Navigate to={currentFlow[i].path} replace />;
      }
    }
    return <Navigate to="/my-profile" replace />;
  }

  return (
    <>
      {render(goBack, goNext)}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={clearToast} />
      )}
    </>
  );
};

export default FlowRoute;
